import { localizedStrings } from '@core/strings'
import React, { useState } from 'react'
import { useModal } from 'webapp/components/common/GlobalModal'
import {
  Avatar,
  DialogActions,
  DialogTitle,
  IconButton,
  DialogContent,
  Button,
  LoadingButton,
  Card,
} from '@common/material'
import { Box, Flex, Text } from '@fundamentals'
import ClearIcon from '@mui/icons-material/Clear'
import CaptureSignature from '@common/CaptureSignature'
import { useGetProfile } from '@core/react-query/features/profile/hooks/useGetProfile'
import { Permit } from '@core/entities/permit/Permit'
import { ParticipantRole } from '@core/entities/participant/Participant'
import { useRespondToBriefing } from '@core/react-query/features/permits/permit'
import { ModalDialogContainer } from '@common/modal'
import { useToast } from '@core/toast'

interface ComponentType {
  permit: Permit
  role: ParticipantRole
}

export const AcceptBriefingModal: React.FC<ComponentType> = ({
  permit,
  role,
}) => {
  const respondToBriefing = useRespondToBriefing()
  const { hideModal } = useModal()
  const { data: profile } = useGetProfile()
  const toast = useToast()
  const [signature, setSignature] = useState<File | null>(null)

  return (
    <ModalDialogContainer>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.signAndSubmit}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {role.briefingText && (
          <Card sx={{ p: 2 }}>
            <Text fontSize={12} color='text.secondary' mb={0.5}>
              {localizedStrings.briefingInformation}
            </Text>
            <Text dangerouslySetInnerHTML={{ __html: role.briefingText }} />
          </Card>
        )}
        <Flex alignItems='center' gap={1.5} mt={2}>
          <Avatar>{profile?.getInitials()}</Avatar>
          <Box>
            <Text>{profile?.getFullName()}</Text>
            <Text fontSize={12} color='grey'>
              {[role.name, profile?.getCompanies()?.[0]?.name]
                .filter(Boolean)
                .join(' - ')}
            </Text>
          </Box>
        </Flex>
        <CaptureSignature inputProps={{ onChange: setSignature }} />
      </DialogContent>

      <DialogActions sx={{ pb: 2, pr: 3 }}>
        <Button variant='outlined' onClick={hideModal}>
          {localizedStrings.cancel}
        </Button>
        <LoadingButton
          variant='contained'
          loading={respondToBriefing.isLoading}
          disabled={!signature}
          onClick={() => {
            respondToBriefing.mutate(
              {
                permitId: permit.id,
                templateVersionRoleId: role.roleId,
                decision: 'ACCEPT',
                signatureFile: signature,
              },
              {
                onSuccess: () => {
                  toast.success(localizedStrings.briefingAccepted)
                  hideModal()
                },
                onError: (e) => toast.error(e.message),
              },
            )
          }}
          data-test='accept-briefing-submit'
        >
          {localizedStrings.submit}
        </LoadingButton>
      </DialogActions>
    </ModalDialogContainer>
  )
}
